/**
 * dcent-tabs.js — reusable, framework-free tab component (self-hydrating).
 *
 * Promotes the EComposer `tabs__nav` / `data-target-id` pattern to a native,
 * accessible (WAI-ARIA tablist) component shared across content-rich pages.
 *
 * Markup contract:
 *   <div class="dcent-tabs" data-dcent-tabs>            <!-- add --showcase for side-by-side nav+image -->
 *     <div class="dcent-tabs__nav" role="tablist">
 *       <button class="dcent-tabs__tab" role="tab" id="tab-p1"
 *               data-dcent-tab="p1" aria-controls="p1" aria-selected="true">…</button>
 *       …
 *     </div>
 *     <div class="dcent-tabs__panels">
 *       <div class="dcent-tabs__panel" id="p1" role="tabpanel" aria-labelledby="tab-p1">…</div>
 *       …
 *     </div>
 *   </div>
 *
 * The tab whose aria-selected="true" (else the first tab) is active on load.
 * Click or ArrowLeft/Right/Up/Down switches tabs; the active panel is shown,
 * the rest hidden. On the `--showcase` variant, pointer hover (mouseenter) also
 * switches tabs — restoring the EComposer vertical-tab hover-to-expand behavior
 * (click stays available). Idempotent — re-running init() on an already-wired
 * group is a no-op.
 */

export function activateTab(group, tab) {
  const tabs = Array.from(group.querySelectorAll(".dcent-tabs__tab"));
  const panels = Array.from(group.querySelectorAll(".dcent-tabs__panel"));
  const targetId = tab.getAttribute("data-dcent-tab");
  tabs.forEach((t) => {
    const selected = t === tab;
    t.setAttribute("aria-selected", selected ? "true" : "false");
    t.classList.toggle("is-active", selected);
    t.tabIndex = selected ? 0 : -1;
  });
  panels.forEach((p) => {
    const show = p.id === targetId;
    p.classList.toggle("is-active", show);
    p.hidden = !show;
  });
}

export function initTabs(root = document) {
  root.querySelectorAll("[data-dcent-tabs]").forEach((group) => {
    if (group.__dcentTabsInit) return;
    group.__dcentTabsInit = true;

    const tabs = Array.from(group.querySelectorAll(".dcent-tabs__tab"));
    if (!tabs.length) return;

    // --showcase (vertical nav + image) restores the EComposer hover-to-switch
    // interaction; other variants (e.g. dapp horizontal tabs) stay click-only.
    const hoverActivate = group.classList.contains("dcent-tabs--showcase");

    tabs.forEach((tab, i) => {
      tab.addEventListener("click", () => activateTab(group, tab));
      if (hoverActivate)
        tab.addEventListener("mouseenter", () => {
          if (!tab.classList.contains("is-active")) activateTab(group, tab);
        });
      tab.addEventListener("keydown", (e) => {
        let next = null;
        if (e.key === "ArrowRight" || e.key === "ArrowDown")
          next = (i + 1) % tabs.length;
        else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
          next = (i - 1 + tabs.length) % tabs.length;
        if (next === null) return;
        e.preventDefault();
        tabs[next].focus();
        activateTab(group, tabs[next]);
      });
    });

    const initial =
      tabs.find((t) => t.getAttribute("aria-selected") === "true") || tabs[0];
    activateTab(group, initial);
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initTabs());
}
